import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { components } from 'react-select';
import { X, Loader, AlertCircle, Plus, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { Select } from '../common/Select.js';
import { Tooltip } from '../../admin/components/shared/Tooltip.tsx';
import { fetchUnmappedFlowchartRisks, attachManualRiskToFlowchart, detachManualRiskFromFlowchart, fetchFlowchartVersions } from '../../request/flowchart';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { getRiskCategoryLabel } from '../../constants/riskCategories';
import { TENANT_PATHS } from '../../routing/paths.ts';
import {
  resolveDisplayOnlyRiskIdentifier,
  resolveMetaBarSystemRiskId,
  truncateProcessDisplayName,
  formatRiskSourceLabel,
  LINE_CLAMP_2_CLASS,
} from '../../features/risks/utils/riskDetailsUtils.ts';
import { PaginatedSelectMenuList } from '../../features/risks/components/PaginatedSelectMenuList.tsx';
import { ListPageSearchInput } from '../../shared/list-page/ListPageSearchInput.tsx';

const RISK_LEVEL_STYLES = {
  High: { card: 'border-red-200 bg-red-50/60', badge: 'bg-red-600 text-white' },
  Medium: { card: 'border-amber-200 bg-amber-50/60', badge: 'bg-amber-500 text-white' },
  Low: { card: 'border-green-200 bg-green-50/60', badge: 'bg-green-600 text-white' },
};

const SUBPROCESS_SELECT_STYLES = {
  placeholder: (base, state) => ({
    ...base,
    fontWeight: 600,
    color: state.isFocused ? '#9ca3af' : 'black',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 100,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 100,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 240,
    overflowY: 'auto',
  }),
  option: (base) => ({
    ...base,
    whiteSpace: 'normal',
    overflow: 'hidden',
  }),
};

function ClampedSubprocessOption(optionProps) {
  const label = String(optionProps.label ?? '');
  return (
    <components.Option {...optionProps} className="app-select__option--clamped">
      <Tooltip content={label} side="right">
        <div className="w-full min-w-0">
          <span className={`text-sm ${LINE_CLAMP_2_CLASS}`}>{label}</span>
        </div>
      </Tooltip>
    </components.Option>
  );
}

const SUBPROCESS_SELECT_COMPONENTS = {
  MenuList: PaginatedSelectMenuList,
  Option: ClampedSubprocessOption,
};

const SIDE_PANEL_RISK_LEVEL_FILTER_OPTIONS = [
  { label: 'Level', value: 'all' },
  { label: 'High', value: 'High' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Low', value: 'Low' },
];

const SIDE_PANEL_SOURCE_FILTER_OPTIONS = [
  { label: 'Source', value: 'all' },
  { label: 'Manual', value: 'manual' },
  { label: 'AI Assisted', value: 'ai_assisted' },
  { label: 'Batch Import', value: 'batch_import' },
];

const SIDE_PANEL_CATEGORY_FILTER_OPTIONS = [
  { label: 'Category', value: 'all' },
  { label: 'SOX', value: 'SOX' },
  { label: 'Compliance', value: 'Compliance' },
  { label: 'Operational', value: 'Operational' },
  { label: 'Strategic', value: 'Strategic' },
  { label: 'Fraud', value: 'Fraud' },
];

const SIDE_PANEL_ORIGIN_VERSION_FILTER_ALL = 'all';

function SidePanelFilterDropdown({
  value,
  options,
  onChange,
  disabled = false,
  ariaLabel,
  menuMinWidthClass = 'min-w-[9rem]',
  wrapperClassName = 'relative min-w-0 flex-1',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const selectedOption =
    options.find((option) => option.value === value) || options[0] || null;
  const selectedLabel = selectedOption?.label || 'Select';

  return (
    <div className={wrapperClassName} ref={menuRef}>
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          setIsOpen((prev) => !prev);
        }}
        disabled={disabled}
        className="inline-flex h-9 w-full cursor-pointer items-center justify-between rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white px-2.5 text-xs font-medium text-slate-700 shadow-sm transition-all duration-150 hover:border-slate-300 hover:shadow focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-70"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
      >
        <span className="truncate pr-1">{selectedLabel}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen ? (
        <div
          className={`absolute left-0 top-[2.35rem] z-50 w-full overflow-hidden rounded-xl border border-blue-100 bg-white py-1.5 shadow-2xl ring-1 ring-blue-100/70 ${menuMinWidthClass}`}
        >
          <div className="max-h-56 overflow-auto px-1" role="listbox" aria-label={ariaLabel}>
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={`${option.value}-${option.label}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`flex w-full cursor-pointer items-center rounded-lg px-2.5 py-1.5 text-left text-xs transition-colors ${
                    isSelected
                      ? 'bg-blue-600 font-semibold text-white'
                      : 'font-medium text-slate-700 hover:bg-blue-50'
                  }`}
                >
                  <span className="truncate">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function getNodeLabel(node, flowchartNodeById) {
  const fromFlowchart = flowchartNodeById.get(node?.id);
  const description =
    node?.nodeDescription ||
    fromFlowchart?.nodeDescription ||
    '';
  if (description.trim()) return description.trim();
  const type = node?.nodeType || fromFlowchart?.nodeType || '';
  if (type.trim()) return type.trim();
  return node?.id || 'Subprocess';
}

function SubprocessLinkPicker({ options, onSelect, disabled, isOpen, onClose }) {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [selectKey, setSelectKey] = useState(0);

  const selectOptions = useMemo(
    () => options.map((node) => ({ value: node.id, label: node.label })),
    [options]
  );

  const filteredOptions = useMemo(() => {
    const query = searchInputValue.trim().toLowerCase();
    if (!query) return selectOptions;
    return selectOptions.filter((option) =>
      String(option.label).toLowerCase().includes(query)
    );
  }, [searchInputValue, selectOptions]);

  if (!isOpen || !options.length) return null;

  return (
    <div className="mt-1.5 w-full min-w-0">
      <Select
        key={selectKey}
        value={null}
        onChange={(value) => {
          if (!value) return;
          onSelect(value);
          setSearchInputValue('');
          setSelectKey((current) => current + 1);
          onClose?.();
        }}
        isSearchable
        isDisabled={disabled}
        menuIsOpen
        autoFocus
        inputValue={searchInputValue}
        onInputChange={(value, meta) => {
          if (meta.action === 'input-change') setSearchInputValue(value);
        }}
        onMenuClose={() => {
          setSearchInputValue('');
          onClose?.();
        }}
        noOptionsMessage={() =>
          searchInputValue.trim() ? 'No subprocesses found' : 'No subprocesses available'
        }
        placeholder="Select subprocess..."
        placeholderOnFocus="Type to search subprocess..."
        placeholderOnBlur="Select subprocess..."
        components={SUBPROCESS_SELECT_COMPONENTS}
        hasMoreOptions={false}
        loadingMoreOptions={false}
        styles={SUBPROCESS_SELECT_STYLES}
        options={filteredOptions}
        menuPlacement="auto"
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        menuPosition="fixed"
        className="w-full min-w-0"
        aria-label="Link to subprocess"
      />
    </div>
  );
}

function CatalogRiskCard({
  risk,
  flowchartNodeById,
  availableNodes,
  onSelectNode,
  onUnlinkNode,
  attachingNodeId,
  unlinkingNodeId,
}) {
  const displayRiskId =
    resolveDisplayOnlyRiskIdentifier({
      client_risk_id: risk.clientRiskId,
      clientRiskId: risk.clientRiskId,
      risk_id: risk.riskId,
      riskId: risk.riskId,
      id: risk.id,
    }) || risk.id;
  const riskDetailsRiskId = resolveMetaBarSystemRiskId({
    client_risk_id: risk.clientRiskId,
    risk_id: risk.riskId,
    id: risk.id,
  });
  const riskDetailsPath = riskDetailsRiskId
    ? TENANT_PATHS.SOX_RISKS_VIEW.replace(':riskId', encodeURIComponent(riskDetailsRiskId))
    : null;
  const level = risk.riskLevel || 'Medium';
  const sourceLabel = formatRiskSourceLabel(risk.source);
  const sourceVersionNumber = Number(risk.sourceRefVersionNumber);
  const hasSourceVersion = Number.isFinite(sourceVersionNumber) && sourceVersionNumber > 0;
  const sourceVersionStatus = String(risk.sourceRefVersionStatus ?? '').trim();
  const sourceVersionLabel = hasSourceVersion
    ? `v${sourceVersionNumber}${
        sourceVersionStatus
          ? ` · ${sourceVersionStatus.charAt(0).toUpperCase()}${sourceVersionStatus.slice(1).toLowerCase()}`
          : ''
      }`
    : null;
  const levelStyles = RISK_LEVEL_STYLES[level] || {
    card: 'border-slate-200 bg-white',
    badge: 'bg-slate-200 text-slate-700',
  };
  const linkedNodes = Array.isArray(risk.nodes) ? risk.nodes : [];
  const linkedControls = Array.isArray(risk.controls) ? risk.controls : [];
  const riskDescription = (risk.riskDescription || '').trim() || '—';
  const [isSubprocessPickerOpen, setIsSubprocessPickerOpen] = useState(false);
  const hasAvailableSubprocesses = availableNodes.length > 0;
  const isAttaching = Boolean(attachingNodeId);
  const isUnlinking = Boolean(unlinkingNodeId);

  const handleSelectSubprocess = useCallback(
    (nodeId) => {
      onSelectNode(risk, nodeId);
      setIsSubprocessPickerOpen(false);
    },
    [onSelectNode, risk]
  );

  return (
    <div className={`rounded-lg border p-3 shadow-sm ${levelStyles.card}`}>
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        {riskDetailsPath ? (
          <Link
            to={riskDetailsPath}
            className="text-xs font-semibold text-blue-700 hover:text-blue-800 hover:underline"
          >
            {displayRiskId}
          </Link>
        ) : (
          <span className="text-xs font-semibold text-slate-700">{displayRiskId}</span>
        )}
        {sourceLabel ? (
          <span className="inline-flex items-center rounded border border-blue-100 bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-blue-700">
            {sourceLabel}
          </span>
        ) : null}
        {sourceVersionLabel ? (
          <span
            className="inline-flex items-center rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-slate-600"
            title="Origin flowchart version"
          >
            {sourceVersionLabel}
          </span>
        ) : null}
        <span className={`ml-auto rounded px-2 py-0.5 text-xs font-semibold ${levelStyles.badge}`}>
          {level}
        </span>
      </div>

      {risk.riskCategory ? (
        <p className="mb-1 text-xs text-slate-500">{getRiskCategoryLabel(risk.riskCategory)}</p>
      ) : null}

      {riskDescription === '—' ? (
        <p className="text-xs text-slate-800">—</p>
      ) : (
        <Tooltip content={riskDescription} side="top">
          <p className={`text-xs leading-snug text-slate-800 ${LINE_CLAMP_2_CLASS}`}>{riskDescription}</p>
        </Tooltip>
      )}

      {linkedControls.length > 0 ? (
        <div className="mt-2.5">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Controls
          </p>
          <div className="flex flex-wrap gap-1.5">
            {linkedControls.map((control) => {
              const displayControlId =
                (control.clientControlId || control.controlId || '').trim() || '—';
              const controlPathId = (control.controlId || '').trim() || null;
              const controlDetailsPath = controlPathId
                ? TENANT_PATHS.SOX_CONTROLS_VIEW.replace(
                    ':id',
                    encodeURIComponent(controlPathId)
                  )
                : null;
              const controlTitle = (control.title || '').trim() || displayControlId;
              const pillClassName =
                'inline-flex max-w-full min-w-0 items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-800';

              return (
                <Tooltip
                  key={control.id || `${displayControlId}-${controlTitle}`}
                  content={controlTitle}
                  side="top"
                >
                  {controlDetailsPath ? (
                    <Link
                      to={controlDetailsPath}
                      className={`${pillClassName} hover:bg-indigo-100 hover:text-indigo-900`}
                    >
                      <span className="truncate">{displayControlId}</span>
                    </Link>
                  ) : (
                    <span className={pillClassName}>
                      <span className="truncate">{displayControlId}</span>
                    </span>
                  )}
                </Tooltip>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="mt-2.5">
        <div className="mb-1 flex items-center gap-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Subprocesses
          </p>
          {hasAvailableSubprocesses ? (
            <button
              type="button"
              onClick={() => setIsSubprocessPickerOpen((open) => !open)}
              disabled={isAttaching}
              className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 transition-colors hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Link to subprocess"
              title="Link to subprocess"
            >
              <Plus className="h-3 w-3" />
            </button>
          ) : null}
        </div>
        {linkedNodes.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {linkedNodes.map((node) => {
              const fullLabel = getNodeLabel(node, flowchartNodeById);
              const shortLabel = truncateProcessDisplayName(fullLabel, 40);
              const isThisNodeUnlinking = unlinkingNodeId === node.id;
              return (
                <Tooltip key={`${risk.id}-${node.id}`} content={fullLabel} side="top">
                  <span className="inline-flex max-w-full min-w-0 items-center gap-1 rounded-full border border-teal-200 bg-teal-50 py-0.5 pl-2 pr-1 text-[11px] font-medium text-teal-800">
                    <span className="truncate">{shortLabel}</span>
                    <button
                      type="button"
                      onClick={() => onUnlinkNode(risk, node.id)}
                      disabled={isAttaching || isUnlinking}
                      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-teal-700 transition-colors hover:bg-teal-100 hover:text-teal-900 disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label={`Unlink from ${fullLabel}`}
                      title="Unlink from subprocess"
                    >
                      {isThisNodeUnlinking ? (
                        <Loader className="h-3 w-3 animate-spin" />
                      ) : (
                        <X className="h-3 w-3" />
                      )}
                    </button>
                  </span>
                </Tooltip>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-slate-500">Not linked to any subprocess yet.</p>
        )}

        <SubprocessLinkPicker
          options={availableNodes}
          onSelect={handleSelectSubprocess}
          disabled={isAttaching}
          isOpen={isSubprocessPickerOpen}
          onClose={() => setIsSubprocessPickerOpen(false)}
        />
      </div>
    </div>
  );
}

export default function FlowchartCatalogRisksSidePanel({
  isOpen,
  onClose,
  processUuid,
  flowchartVersionId,
  cycleId,
  processId,
  versionOptions = [],
  flowchartNodes = [],
  onRiskAttached,
  auditYearId,
}) {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attachingNodeId, setAttachingNodeId] = useState(null);
  const [unlinkingNodeId, setUnlinkingNodeId] = useState(null);
  const [filterRiskLevel, setFilterRiskLevel] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterRiskCategory, setFilterRiskCategory] = useState('all');
  const [filterOriginVersionId, setFilterOriginVersionId] = useState(SIDE_PANEL_ORIGIN_VERSION_FILTER_ALL);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [originVersionOptions, setOriginVersionOptions] = useState([
    { label: 'All Origin Versions', value: SIDE_PANEL_ORIGIN_VERSION_FILTER_ALL },
  ]);
  const [loadingOriginVersions, setLoadingOriginVersions] = useState(false);

  const sidePanelFilters = useMemo(
    () => ({
      riskLevel: filterRiskLevel,
      source: filterSource,
      riskCategory: filterRiskCategory,
      sourceRefVersionId: filterOriginVersionId,
      search: debouncedSearchQuery,
    }),
    [filterRiskLevel, filterSource, filterRiskCategory, filterOriginVersionId, debouncedSearchQuery]
  );

  const hasActiveSidePanelFilters =
    filterRiskLevel !== 'all' ||
    filterSource !== 'all' ||
    filterRiskCategory !== 'all' ||
    filterOriginVersionId !== SIDE_PANEL_ORIGIN_VERSION_FILTER_ALL ||
    debouncedSearchQuery !== '';

  const flowchartNodeById = useMemo(() => {
    const map = new Map();
    flowchartNodes.forEach((node) => {
      if (node?.id) map.set(node.id, node);
    });
    return map;
  }, [flowchartNodes]);

  const loadRisks = useCallback(async ({ silent = false } = {}) => {
    if (!processUuid || !flowchartVersionId) {
      setRisks([]);
      setError('Process or flowchart version is unavailable.');
      return;
    }
    if (!silent) {
      setLoading(true);
    }
    setError('');
    try {
      const nextRisks = await fetchUnmappedFlowchartRisks(
        processUuid,
        flowchartVersionId,
        sidePanelFilters
      );
      setRisks(nextRisks);
    } catch (err) {
      console.error('Failed to load catalog risks:', err);
      setRisks([]);
      setError(err?.response?.data?.error || err?.message || 'Failed to load risks');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [processUuid, flowchartVersionId, sidePanelFilters]);

  useEffect(() => {
    if (!isOpen) {
      setOriginVersionOptions([
        { label: 'All Origin Versions', value: SIDE_PANEL_ORIGIN_VERSION_FILTER_ALL },
      ]);
      return;
    }

    const buildOriginVersionOptions = (versions) => {
      const versionItems = (Array.isArray(versions) ? versions : [])
        .map((option) => {
          const versionId =
            option?.flowchartVersionId ||
            option?.id ||
            option?.flowchart_version_id ||
            option?.versionId ||
            null;
          if (!versionId) return null;
          return {
            label: option.label || `v${option.value ?? option.versionNumber ?? '?'}`,
            value: versionId,
          };
        })
        .filter(Boolean);

      return [
        { label: 'All Origin Versions', value: SIDE_PANEL_ORIGIN_VERSION_FILTER_ALL },
        ...versionItems,
      ];
    };

    if (Array.isArray(versionOptions) && versionOptions.length > 0) {
      setOriginVersionOptions(buildOriginVersionOptions(versionOptions));
      return;
    }

    if (!cycleId || !processId) {
      setOriginVersionOptions([
        { label: 'All Origin Versions', value: SIDE_PANEL_ORIGIN_VERSION_FILTER_ALL },
      ]);
      return;
    }

    let cancelled = false;
    setLoadingOriginVersions(true);
    fetchFlowchartVersions(cycleId, processId)
      .then((versions) => {
        if (cancelled) return;
        setOriginVersionOptions(buildOriginVersionOptions(versions));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load flowchart versions for side panel filter:', err);
        setOriginVersionOptions([
          { label: 'All Origin Versions', value: SIDE_PANEL_ORIGIN_VERSION_FILTER_ALL },
        ]);
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingOriginVersions(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, cycleId, processId, versionOptions]);

  const handleResetSidePanelFilters = useCallback(() => {
    setFilterRiskLevel('all');
    setFilterSource('all');
    setFilterRiskCategory('all');
    setFilterOriginVersionId(SIDE_PANEL_ORIGIN_VERSION_FILTER_ALL);
    setSearchQuery('');
    setDebouncedSearchQuery('');
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadRisks();
  }, [isOpen, loadRisks]);

  const handleSelectNode = useCallback(async (risk, nodeId) => {
    if (!processUuid || !flowchartVersionId || !risk?.id || !nodeId) return;
    setAttachingNodeId(nodeId);
    try {
      await attachManualRiskToFlowchart({
        processId: processUuid,
        flowchartVersionId,
        riskId: risk.id,
        nodeIds: [nodeId],
        auditYearId: auditYearId || undefined,
      });
      toast.success('Risk linked to subprocess');
      await loadRisks({ silent: true });
      await onRiskAttached?.({ action: 'attach', risk, nodeId });
    } catch (err) {
      const message = resolve403ErrorMessage(
        err,
        err?.response?.data?.error || err?.message || 'Failed to link risk to subprocess'
      );
      toast.error(message);
    } finally {
      setAttachingNodeId(null);
    }
  }, [processUuid, flowchartVersionId, loadRisks, onRiskAttached, auditYearId]);

  const handleUnlinkNode = useCallback(async (risk, nodeId) => {
    if (!processUuid || !flowchartVersionId || !risk?.id || !nodeId) return;
    setUnlinkingNodeId(nodeId);
    try {
      await detachManualRiskFromFlowchart({
        processId: processUuid,
        flowchartVersionId,
        riskId: risk.id,
        nodeId,
      });
      toast.success('Risk unlinked from subprocess');
      await loadRisks({ silent: true });
      await onRiskAttached?.({ action: 'detach', risk, nodeId });
    } catch (err) {
      const message = resolve403ErrorMessage(
        err,
        err?.response?.data?.error || err?.message || 'Failed to unlink risk from subprocess'
      );
      toast.error(message);
    } finally {
      setUnlinkingNodeId(null);
    }
  }, [processUuid, flowchartVersionId, loadRisks, onRiskAttached]);

  if (!isOpen) return null;

  return (
    <aside className="flex h-full min-h-0 w-[460px] shrink-0 flex-col overflow-hidden border-l border-slate-200 bg-white">
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Process risks
          </p>
          <h2 className="text-base font-semibold text-slate-900">All risks</h2>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">
            Catalog risks for this process. Subprocesses show where each risk is already linked on
            this flowchart version.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          aria-label="Close risks panel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="shrink-0 space-y-2 border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <ListPageSearchInput
              placeholder="Search ID or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              isDebounce
              onDebouncedChange={setDebouncedSearchQuery}
              inputClassName="bg-slate-50 text-sm h-9 py-1.5"
            />
          </div>
          <SidePanelFilterDropdown
            value={filterRiskCategory}
            options={SIDE_PANEL_CATEGORY_FILTER_OPTIONS}
            onChange={setFilterRiskCategory}
            ariaLabel="Filter by SOX category"
            menuMinWidthClass="min-w-[9rem]"
            wrapperClassName="relative w-[7.5rem] shrink-0"
          />
        </div>
        <div className="flex items-center gap-2">
          <SidePanelFilterDropdown
            value={filterRiskLevel}
            options={SIDE_PANEL_RISK_LEVEL_FILTER_OPTIONS}
            onChange={setFilterRiskLevel}
            ariaLabel="Filter by risk level"
          />
          <SidePanelFilterDropdown
            value={filterSource}
            options={SIDE_PANEL_SOURCE_FILTER_OPTIONS}
            onChange={setFilterSource}
            ariaLabel="Filter by source"
          />
          <SidePanelFilterDropdown
            value={filterOriginVersionId}
            options={originVersionOptions}
            onChange={setFilterOriginVersionId}
            disabled={loadingOriginVersions}
            ariaLabel="Filter by origin flowchart version"
            menuMinWidthClass="min-w-[11rem]"
          />
        </div>
        {hasActiveSidePanelFilters ? (
          <button
            type="button"
            onClick={handleResetSidePanelFilters}
            className="text-xs font-medium text-blue-700 hover:text-blue-800 hover:underline"
          >
            Reset filters
          </button>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500">
            <Loader className="mb-3 h-6 w-6 animate-spin text-blue-600" />
            <p className="text-sm">Loading risks…</p>
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <div className="mb-2 flex items-center gap-2 font-medium">
              <AlertCircle className="h-4 w-4" />
              <span>Could not load risks</span>
            </div>
            <p>{error}</p>
          </div>
        ) : risks.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
            {hasActiveSidePanelFilters
              ? 'No risks match the selected filters or search.'
              : 'No catalog risks found for this process.'}
          </div>
        ) : (
          <div className="space-y-2">
            {risks.map((risk) => {
              const linkedIds = new Set((risk.nodes || []).map((node) => node.id));
              const availableNodes = flowchartNodes
                .filter((node) => node?.id && !linkedIds.has(node.id))
                .map((node) => ({
                  id: node.id,
                  label: node.nodeDescription?.trim() || node.nodeType?.trim() || node.id,
                }));

              return (
                <CatalogRiskCard
                  key={risk.id}
                  risk={risk}
                  flowchartNodeById={flowchartNodeById}
                  availableNodes={availableNodes}
                  onSelectNode={handleSelectNode}
                  onUnlinkNode={handleUnlinkNode}
                  attachingNodeId={attachingNodeId}
                  unlinkingNodeId={unlinkingNodeId}
                />
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
}
